import React, { useEffect, useState, useRef, useCallback } from 'react';
import Card from './Card';
import useTranslation from '../i18n/useTranslation';
import '../styles/pixel-art.css';

const ActionAnimation = ({ actionData, onComplete }) => {
  const { t } = useTranslation();
  const [showCard, setShowCard] = useState(false);
  const [showExplosion, setShowExplosion] = useState(false);
  const canvasRef = useRef(null);
  const particlesRef = useRef([]);
  const animationFrameRef = useRef(null);

  const animate = useCallback((ctx) => {
    ctx.fillStyle = 'rgba(15, 15, 30, 0.2)';
    ctx.fillRect(0, 0, ctx.canvas.width, ctx.canvas.height);

    particlesRef.current.forEach((particle, index) => {
      // Update physics
      particle.vy += particle.gravity;
      particle.x += particle.vx;
      particle.y += particle.vy;
      particle.life -= particle.decay;

      // Draw trail for fireworks
      if (particle.type === 'firework' && particle.trail) {
        particle.trail.push({ x: particle.x, y: particle.y, life: particle.life });
        if (particle.trail.length > 10) particle.trail.shift();

        particle.trail.forEach((point, i) => {
          const alpha = (point.life * i) / particle.trail.length;
          ctx.fillStyle = particle.color + Math.floor(alpha * 255).toString(16).padStart(2, '0');
          ctx.fillRect(point.x, point.y, particle.size * 0.5, particle.size * 0.5);
        });
      }

      // Draw particle
      if (particle.life > 0) {
        ctx.save();

        // Glow effect
        const gradient = ctx.createRadialGradient(
          particle.x, particle.y, 0,
          particle.x, particle.y, particle.size * 2
        );
        gradient.addColorStop(0, particle.color + Math.floor(particle.life * 255).toString(16).padStart(2, '0'));
        gradient.addColorStop(1, particle.color + '00');

        ctx.fillStyle = gradient;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size * 2, 0, Math.PI * 2);
        ctx.fill();

        // Core
        ctx.fillStyle = particle.color;
        ctx.globalAlpha = particle.life;
        ctx.beginPath();
        ctx.arc(particle.x, particle.y, particle.size, 0, Math.PI * 2);
        ctx.fill();

        ctx.restore();
      } else {
        particlesRef.current.splice(index, 1);
      }
    });

    if (particlesRef.current.length > 0) {
      animationFrameRef.current = requestAnimationFrame(() => animate(ctx));
    }
  }, []);

  const initParticles = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    canvas.width = window.innerWidth;
    canvas.height = window.innerHeight;

    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;

    particlesRef.current = [];

    const colors = ['#00d9ff', '#ff006e', '#ffbe0b', '#00ff00'];

    // Create main explosion particles (150 particles)
    for (let i = 0; i < 150; i++) {
      const angle = (Math.PI * 2 * i) / 150;
      const velocity = 3 + Math.random() * 8;
      const size = 3 + Math.random() * 6;

      particlesRef.current.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        size: size,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1,
        decay: 0.01 + Math.random() * 0.015,
        gravity: 0.15 + Math.random() * 0.1,
        type: 'normal'
      });
    }

    // Create secondary explosion wave (100 particles)
    setTimeout(() => {
      for (let i = 0; i < 100; i++) {
        const angle = (Math.PI * 2 * i) / 100;
        const velocity = 5 + Math.random() * 10;
        const size = 4 + Math.random() * 8;

        particlesRef.current.push({
          x: centerX,
          y: centerY,
          vx: Math.cos(angle) * velocity,
          vy: Math.sin(angle) * velocity,
          size: size,
          color: colors[Math.floor(Math.random() * colors.length)],
          life: 1,
          decay: 0.008 + Math.random() * 0.012,
          gravity: 0.2 + Math.random() * 0.15,
          type: 'secondary'
        });
      }
    }, 100);

    // Create big firework bursts (50 large particles)
    for (let i = 0; i < 50; i++) {
      const angle = Math.random() * Math.PI * 2;
      const velocity = 8 + Math.random() * 12;
      const size = 10 + Math.random() * 15;

      particlesRef.current.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        size: size,
        color: colors[Math.floor(Math.random() * colors.length)],
        life: 1,
        decay: 0.005 + Math.random() * 0.008,
        gravity: 0.25,
        type: 'firework',
        trail: []
      });
    }

    // Create sparkles (80 small fast particles)
    for (let i = 0; i < 80; i++) {
      const angle = Math.random() * Math.PI * 2;
      const velocity = 12 + Math.random() * 15;
      const size = 2 + Math.random() * 3;

      particlesRef.current.push({
        x: centerX,
        y: centerY,
        vx: Math.cos(angle) * velocity,
        vy: Math.sin(angle) * velocity,
        size: size,
        color: '#ffffff',
        life: 1,
        decay: 0.015 + Math.random() * 0.02,
        gravity: 0.1,
        type: 'sparkle'
      });
    }

    animate(ctx);
  }, [animate]);

  useEffect(() => {
    if (!actionData) return;

    // Show card after short delay
    const cardTimer = setTimeout(() => {
      setShowCard(true);
    }, 300);

    // Show explosion after card appears
    const explosionTimer = setTimeout(() => {
      if (actionData.damage > 0) {
        setShowExplosion(true);
        initParticles();
      }
    }, 1000);

    // Complete animation after all effects
    const completeTimer = setTimeout(() => {
      if (onComplete) onComplete();
    }, 2500);

    return () => {
      clearTimeout(cardTimer);
      clearTimeout(explosionTimer);
      clearTimeout(completeTimer);
      if (animationFrameRef.current) {
        cancelAnimationFrame(animationFrameRef.current);
      }
    };
  }, [actionData, onComplete, initParticles]);

  if (!actionData) return null;

  return (
    <div className="action-animation-overlay">
      {/* Physics-based particle canvas */}
      {showExplosion && actionData.damage > 0 && (
        <canvas
          ref={canvasRef}
          style={{
            position: 'fixed',
            top: 0,
            left: 0,
            width: '100%',
            height: '100%',
            zIndex: 1,
            pointerEvents: 'none'
          }}
        />
      )}

      <div className="action-animation-content">
        {/* Action description */}
        <div className="action-description pixel-text">
          {actionData.description}
        </div>

        {/* Card drawn animation */}
        {actionData.cardDrawn && showCard && (
          <div className="action-card-container">
            <div className="action-card-label">{t.action.cardDrawn}</div>
            <div className="rotating-card">
              <Card card={actionData.cardDrawn} size="large" />
            </div>
          </div>
        )}

        {/* Damage text */}
        {showExplosion && actionData.damage > 0 && (
          <div className="explosion-container">
            <div className="damage-text pixel-text">
              -{actionData.damage} {t.action.hp}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default ActionAnimation;
